import React, { useState, useEffect, useCallback } from 'react';
import { Avatar, Toast, Mask, Calendar } from 'antd-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { UserOutline } from 'antd-mobile-icons';
import { useStore } from '../store/useStore';
import { getSmokingUnit } from '@puff/shared';
import { SideMenu } from '../components/SideMenu';
import { theme } from '../theme';
import { Edit } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

export const Dashboard: React.FC = () => {
  const { user, todayCount, todayLogs, setTodayLogs, setActiveTab, updateTodayCount, addPuffLog, openSideMenu } = useStore();
  const isViewer = (user as any)?.role === 'viewer';
  const ownerUserId = (user as any)?.ownerUserId;
  // For viewers, use the owner's data
  const targetUserId = isViewer && ownerUserId
    ? ownerUserId
    : user?.id;
  const dailyLimit = user?.dailyPuffLimit ?? 20;
  const unit = getSmokingUnit(user?.smokingType);
  const [lastPuffStr, setLastPuffStr] = useState('00:00:00');
  const [menuVisible, setMenuVisible] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: 'add' | 'remove' } | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<{ displayName: string; pictureUrl: string } | null>(null);

  // Date filter state (range)
  const [rangeStart, setRangeStart] = useState(() => new Date());
  const [rangeEnd, setRangeEnd] = useState(() => new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [tempRangeStart, setTempRangeStart] = useState<Date | null>(null);
  const [tempRangeEnd, setTempRangeEnd] = useState<Date | null>(null);
  const [dateCount, setDateCount] = useState(0);

  const toBangkokDateStr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
  const bangkokStartOfDay = (d: Date) => new Date(toBangkokDateStr(d) + 'T00:00:00+07:00');
  const bangkokEndOfDay = (d: Date) => new Date(toBangkokDateStr(d) + 'T23:59:59.999+07:00');
  const isSameDayFn = (a: Date, b: Date) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const daysBetween = (a: Date, b: Date) => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const aD = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const bD = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((bD.getTime() - aD.getTime()) / msPerDay);
  };

  const isRangeSingleDay = isSameDayFn(rangeStart, rangeEnd);
  const rangeLength = daysBetween(rangeStart, rangeEnd) + 1;
  const isViewingToday = isRangeSingleDay && isSameDayFn(rangeStart, new Date());
  const totalLimit = dailyLimit * rangeLength;
  const percentage = Math.min((dateCount / totalLimit) * 100, 100);

  // Fetch owner info for viewers
  useEffect(() => {
    if (!isViewer || !ownerUserId) return;
    fetch(`${API_BASE_URL}/user/${ownerUserId}`)
      .then(r => r.json())
      .then(data => {
        if (data.user) {
          setOwnerInfo({
            displayName: data.user.displayName || 'ผู้ใช้',
            pictureUrl: data.user.pictureUrl || '',
          });
        }
      })
      .catch(() => { });
  }, [isViewer, ownerUserId]);

  // Display name & picture: owner's if viewer, else own
  const displayName = isViewer && ownerInfo ? ownerInfo.displayName : user?.displayName;
  const displayPic = isViewer && ownerInfo ? ownerInfo.pictureUrl : (user?.pictureUrl || '');

  // Auto-open SideMenu when navigated from tab=profile
  useEffect(() => {
    if (openSideMenu) {
      setMenuVisible(true);
      useStore.setState({ openSideMenu: false });
    }
  }, [openSideMenu]);

  // Set document title to 'หน้าหลัก'
  useEffect(() => {
    document.title = 'หน้าหลัก';
    return () => {
      document.title = 'porddee - ตัวช่วยเลิกสูบ';
    };
  }, []);


  const syncFromServer = useCallback(async () => {
    if (!targetUserId) return;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const res = await fetch(`${API_BASE_URL}/logs/${targetUserId}?startDate=${today.toISOString()}&limit=1000`);
      if (res.ok) {
        const data = await res.json();
        setTodayLogs(data.logs || []);
      }
    } catch (e) {
      console.error('Failed to sync:', e);
    }
  }, [targetUserId, setTodayLogs]);

  useEffect(() => {
    syncFromServer();
    // Viewers poll every 5s for near real-time updates; owners every 30s
    const interval = setInterval(syncFromServer, isViewer ? 5000 : 30000);
    return () => clearInterval(interval);
  }, [syncFromServer, isViewer]);

  // Sync date-filtered data
  useEffect(() => {
    if (isViewingToday) {
      setDateCount(todayCount);
      return;
    }
    if (!targetUserId) return;
    const fetchDateLogs = async () => {
      try {
        const start = bangkokStartOfDay(rangeStart);
        const end = bangkokEndOfDay(rangeEnd);
        const res = await fetch(`${API_BASE_URL}/logs/${targetUserId}?startDate=${start.toISOString()}&endDate=${end.toISOString()}&limit=10000`);
        if (res.ok) {
          const data = await res.json();
          const logs = (data.logs || []).filter((log: any) => {
            const logDate = new Date(log.loggedAt);
            return logDate >= start && logDate <= end;
          });
          setDateCount(logs.reduce((sum: number, log: any) => sum + log.count, 0));
        }
      } catch (e) {
        console.error('Failed to fetch date logs:', e);
      }
    };
    fetchDateLogs();
  }, [rangeStart, rangeEnd, targetUserId, isViewingToday, todayCount]);

  // Update Time Since Last Puff (Ticker) — DD:HH:MM:SS all from same source
  useEffect(() => {
    const updateTime = () => {
      let lastTime: Date | null = null;

      if (todayLogs && todayLogs.length > 0) {
        // Find latest log
        const timestamps = todayLogs.map((l: any) => new Date(l.loggedAt).getTime());
        const maxTime = Math.max(...timestamps);
        lastTime = new Date(maxTime);
      } else if ((user as any)?.lastPuffAt) {
        lastTime = new Date((user as any).lastPuffAt);
      } else if (user?.quitDate) {
        lastTime = new Date(user.quitDate);
      } else if (user?.createdAt) {
        lastTime = new Date(user.createdAt);
      }

      const diffMs = lastTime ? Math.max(0, Date.now() - lastTime.getTime()) : 0;

      const totalSeconds = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hrs = Math.floor((totalSeconds % 86400) / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      // Format as DD:HH:MM:SS
      const f = (n: number) => n.toString().padStart(2, '0');
      setLastPuffStr(`${f(days)}:${f(hrs)}:${f(mins)}:${f(secs)}`);
    };

    updateTime(); // Initial run
    const timer = setInterval(updateTime, 1000); // 1-second interval
    return () => clearInterval(timer); // Cleanup
  }, [todayLogs, user]);

  const handleLogPuff = async (change: number) => {
    if (isViewer) return;
    if (change > 0) {
      if (!user?.id) {
        addPuffLog({ id: crypto.randomUUID(), userId: 'guest', count: 1, loggedAt: new Date() });
        if (navigator.vibrate) navigator.vibrate(50);
        return;
      }
      addPuffLog({ id: crypto.randomUUID(), userId: user.id, count: 1, loggedAt: new Date() });
      if (navigator.vibrate) navigator.vibrate(50);
      try {
        await fetch(`${API_BASE_URL}/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, count: 1 }),
        });
        await syncFromServer();
      } catch (e) {
        console.error('Failed to log puff', e);
        Toast.show({ content: 'บันทึกไม่สำเร็จ', icon: 'fail' });
        syncFromServer();
      }
    } else {
      if (todayCount > 0) {
        updateTodayCount(todayCount - 1);
        Toast.show({ content: 'ลบออก 1 ครั้ง', duration: 1000 });
        if (user?.id) {
          try {
            await fetch(`${API_BASE_URL}/log/${user.id}/last`, { method: 'DELETE' });
            await syncFromServer();
          } catch (e) {
            syncFromServer();
            Toast.show({ content: 'ลบไม่สำเร็จ', icon: 'fail' });
          }
        }
      }
    }
  };

  const getMessage = () => {
    if (dateCount === 0) return { title: isViewingToday ? 'วันนี้ยังไม่ได้สูบเลย!' : 'ไม่มีบันทึกการสูบ', sub: isViewingToday ? 'เก่งมาก ทำต่อไปนะ 💪' : 'ไม่พบข้อมูลในช่วงนี้' };
    if (percentage < 50) return { title: 'อยู่ในเป้าหมาย', sub: 'ค่อยๆ ลดไปทีละนิด 👍' };
    if (percentage < 80) return { title: 'ใกล้ถึงขีดจำกัดแล้ว', sub: 'ลองหายใจลึกๆ ดูนะ 😮‍💨' };
    if (percentage < 100) return { title: 'เกือบถึงขีดจำกัดแล้ว!', sub: 'พยายามหยุดตรงนี้นะ ⚠️' };
    return { title: 'เกินขีดจำกัดแล้ว', sub: 'ไม่เป็นไร พรุ่งนี้เริ่มใหม่ 😔' };
  };

  const msg = getMessage();

  const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  const thaiDateDisplay = (() => {
    if (isRangeSingleDay) {
      return rangeStart.toLocaleDateString('th-TH', { weekday: 'short', day: 'numeric', month: 'short' });
    }
    const s = rangeStart;
    const e = rangeEnd;
    return `${s.getDate()} ${THAI_MONTHS_SHORT[s.getMonth()]} - ${e.getDate()} ${THAI_MONTHS_SHORT[e.getMonth()]}`;
  })();

  const costPerPuff = user?.costPerPuff || 0.5;
  const usualDaily = user?.dailyAmount || 20;
  const moneySaved = Math.max(0, (usualDaily * rangeLength - dateCount)) * costPerPuff;

  const cards = [
    {
      id: 'saved', // Soft Purple (replaces progress)
      icon: '💰',
      label: isViewingToday ? 'ประหยัดวันนี้' : `ประหยัดได้ (${rangeLength} วัน)`,
      value: `฿${Math.round(moneySaved)}`,
      sub: `ปกติ ${usualDaily} ${unit}/วัน`,
      onClick: () => setActiveTab('money'),
    },
    {
      id: 'plan', // Uses Vibrant Purple
      icon: '🎯',
      label: 'แผนเลิกสูบ',
      value: 'ดูแผน',
      sub: 'กดเพื่อดู',
      onClick: () => setActiveTab('community'),
    },
    {
      id: 'stats',
      icon: '📊',
      label: 'สถิติ',
      value: 'ดูข้อมูล',
      sub: 'วิเคราะห์เชิงลึก',
      onClick: () => setActiveTab('stats'),
    },
    {
      id: 'explore',
      icon: '🏆',
      label: 'สถิติที่ดีที่สุด',
      value: 'ดูสถิติ',
      sub: 'ทำลายสถิติของตัวเอง!',
      onClick: () => setActiveTab('explore'),
    }
  ];

  // Helper to split time for display (DD:HH:MM:SS)
  const [dd, hh, mm, ss] = lastPuffStr.split(':');

  return (
    <>

      <div
        className="page"
        style={{
          height: '100dvh', // Force full viewport height
          background: '#ffffff',
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Update: Reduced bottom padding to match the COMPACTED timer
          padding: '8px 24px calc(env(safe-area-inset-bottom) + 120px)',
          boxSizing: 'border-box',
          fontFamily: '"Prompt", sans-serif',
        }}
      >
        {/* ── VIEWER BADGE ── */}
        {isViewer && (
          <div style={{
            background: 'linear-gradient(135deg, #fce7f3, #fdf2f8)',
            border: '1px solid #f9a8d4',
            borderRadius: 14,
            padding: '8px 14px',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 16 }}>👀</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#be185d' }}>โหมดผู้ดูแล</div>
              <div style={{ fontSize: 11, color: '#9d174d' }}>กำลังดูข้อมูลของ {displayName || 'เพื่อน'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: '#22c55e',
                animation: 'pulse 2s infinite',
              }} />
              <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>สด</span>
            </div>
          </div>
        )}

        {/* ── HEADER (Compact) ── */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12, flexShrink: 0, zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setMenuVisible(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', background: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.07)', overflow: 'hidden'
                }}>
                  <Avatar src={displayPic} style={{ '--size': '48px' }} fallback={<UserOutline style={{ fontSize: 24, color: '#94a3b8' }} />} />
                </div>
                <div style={{
                  position: 'absolute', bottom: -2, right: -4,
                  width: 22, height: 22, borderRadius: '50%',
                  background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <Edit size={12} color="white" strokeWidth={2.5} />
                </div>
              </div>

              <div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {displayName ? displayName.split(' ')[0] : 'สวัสดี'} 🌟
                </h1>
              </div>
            </button>
          </div>

          {/* Date picker button */}
          <button
            onClick={() => { setTempRangeStart(rangeStart); setTempRangeEnd(rangeEnd); setCalendarOpen(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 20,
              background: isViewingToday ? 'white' : `linear-gradient(135deg, ${theme.primarySoft}, white)`,
              border: isViewingToday ? '1px solid #e2e8f0' : `1px solid ${theme.primarySoft}`,
              color: '#0f172a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            📅 {thaiDateDisplay} ▾
          </button>
        </header>

        {/* ── TODAY LABEL ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 2, flexShrink: 0, zIndex: 1,
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>
            {isViewer ? `การสูบบุหรี่ของ ${displayName?.split(' ')[0] || 'เพื่อน'}` : 'การสูบบุหรี่'}
          </h2>
        </div>

        {/* ── MAIN CARD (Purple) - Compacted ── */}
        <div style={{
          background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 100%)',
          borderRadius: 26, padding: '16px 20px', marginBottom: 6,
          position: 'relative', zIndex: 1, flexShrink: 0,
          boxShadow: '0 8px 26px rgba(var(--primary-rgb), 0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginBottom: 2 }}>{isViewingToday ? 'สถานะวันนี้' : `สถานะ ${thaiDateDisplay}`}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4, lineHeight: 1.2 }}>{msg.title}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 12 }}>{msg.sub}</div>
            {!isViewer && isViewingToday && (
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => { if (todayCount > 0) setConfirmAction({ type: 'remove' }); }} style={{
                  width: 42, height: 42, borderRadius: 14, background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: 24, fontWeight: 300, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>-</button>
                <button onClick={() => setConfirmAction({ type: 'add' })} style={{
                  width: 42, height: 42, borderRadius: 14, background: 'white',
                  border: 'none', color: 'var(--primary)', fontSize: 24, fontWeight: 500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.12)'
                }}>+</button>
              </div>
            )}
          </div>

          {/* Circle Progress - Compacted */}
          <div style={{ width: 100, height: 100, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="100" height="100" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
              <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" />
              <circle cx="50" cy="50" r="42" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 42} strokeDashoffset={2 * Math.PI * 42 * (1 - percentage / 100)}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
            </svg>
            <div style={{ textAlign: 'center', color: 'white', zIndex: 1 }}>
              <div style={{ fontSize: 'clamp(24px, 7vw, 28px)', fontWeight: 800, lineHeight: 1.1 }}>{dateCount}</div>
              <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600 }}>/{totalLimit}</div>
            </div>
          </div>
        </div>

        {/* ── SELECT DATA LABEL ── */}
        <div style={{ marginBottom: 4, flexShrink: 0, zIndex: 1 }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: '#0f172a' }}>
            {isViewer ? `ข้อมูลของ ${displayName?.split(' ')[0] || 'เพื่อน'}` : 'ข้อมูลของคุณ'}
          </h3>
        </div>

        {/* ── 4 CARDS GRID (Internal Compact) ── */}
        <div style={{
          flex: 1,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
          minHeight: 'min-content',
          zIndex: 1,
        }}>
          {cards.map((card) => {
            const isPlan = card.id === 'plan';
            const isSaved = card.id === 'saved'; // Soft Purple for saved card

            // Default Style (White)
            let bg = 'white';
            let border = '2px solid #F1F5F9';
            let titleColor = '#94a3b8';
            let valueColor = '#0f172a';
            let subColor = '#94a3b8';
            let iconBg = '#f8fafc';
            let iconRadius: string | number = 10;
            let arrowBg = '#f1f5f9';
            let arrowColor = '#cbd5e1';

            if (isPlan) {
              // Vibrant Purple (Adobe Style)
              bg = 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 100%)';
              border = 'none';
              titleColor = 'rgba(255,255,255,0.9)';
              valueColor = 'white';
              subColor = 'rgba(255,255,255,0.7)';
              iconBg = 'rgba(255,255,255,0.2)';
              arrowBg = 'rgba(255,255,255,0.2)';
              arrowColor = 'white';
            } else if (isSaved) {
              // Soft Purple (same as old progress card)
              bg = 'linear-gradient(160deg, var(--primary-soft) 0%, #ffffff 100%)';
              border = 'none';
              titleColor = 'var(--primary)';
              valueColor = 'var(--primary-deep)';
              subColor = 'var(--primary)';
              iconBg = 'white';
              iconRadius = '50%';
              arrowBg = 'var(--primary-soft)';
              arrowColor = 'var(--primary)';
            } else if (card.id === 'explore') {
              // Teal / Deep Cyan
              bg = 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)';
              border = 'none';
              titleColor = 'rgba(255,255,255,0.9)';
              valueColor = 'white';
              subColor = 'rgba(204,253,245,0.8)';
              iconBg = 'rgba(255,255,255,0.15)';
              arrowBg = 'rgba(255,255,255,0.2)';
              arrowColor = 'white';
            }

            return (
              <div key={card.id} onClick={card.onClick} style={{
                background: bg,
                borderRadius: 24,
                padding: '16px', // Compacted padding
                boxShadow: 'none',
                border: border,
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                position: 'relative', overflow: 'hidden'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: titleColor }}>
                    {card.label}
                  </div>
                  <div style={{
                    fontSize: 10, fontWeight: 700,
                    color: arrowColor,
                    background: arrowBg,
                    width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>↗</div>
                </div>

                <div>
                  <div style={{
                    width: 30, height: 30, borderRadius: iconRadius,
                    background: iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, marginBottom: 4
                  }}>
                    {card.icon}
                  </div>

                  <div style={{ fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 800, color: valueColor, lineHeight: 1.2, wordBreak: 'break-word' }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: subColor, marginTop: 2 }}>
                    {card.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── BOTTOM NAV (Spread Timer) ── */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'white',
          borderTop: '1px solid #e2e8f0',
          padding: '10px 12px calc(env(safe-area-inset-bottom) + 8px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          zIndex: 100,
          boxShadow: '0 -4px 24px rgba(0,0,0,0.04)'
        }}>
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 700, marginBottom: 8 }}>
            หยุดสูบมาแล้ว
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: 360, margin: '0 auto',
            fontSize: 'clamp(24px, 7.5vw, 36px)', color: 'var(--primary)', fontWeight: 800,
            lineHeight: 1, letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums'
          }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div>{dd}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginTop: 4 }}>วัน</div>
            </div>
            <div style={{ fontSize: 'clamp(16px, 5vw, 24px)', opacity: 0.3, marginBottom: 16, marginInline: 'clamp(2px, 1vw, 6px)' }}>:</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div>{hh}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginTop: 4 }}>ชั่วโมง</div>
            </div>
            <div style={{ fontSize: 'clamp(16px, 5vw, 24px)', opacity: 0.3, marginBottom: 16, marginInline: 'clamp(2px, 1vw, 6px)' }}>:</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div>{mm}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginTop: 4 }}>นาที</div>
            </div>
            <div style={{ fontSize: 'clamp(16px, 5vw, 24px)', opacity: 0.3, marginBottom: 16, marginInline: 'clamp(2px, 1vw, 6px)' }}>:</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div>{ss}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginTop: 4 }}>วินาที</div>
            </div>
          </div>
        </div>
      </div>


      {/* ── SIDE MENU DRAWER (Framer Motion) ── */}
      <AnimatePresence>
        {menuVisible && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMenuVisible(false)}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                zIndex: 2000,
              }}
            />
            {/* Drawer Panel */}
            <motion.div
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.8 }}
              style={{
                position: 'fixed', top: 0, bottom: 0, left: 0,
                width: '85vw', maxWidth: 380,
                background: 'white', zIndex: 2001,
                boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
              }}
            >
              <SideMenu onClose={() => setMenuVisible(false)} setActiveTab={setActiveTab} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── CONFIRM MODAL ── */}
      {confirmAction && (
        <>
          <div onClick={() => setConfirmAction(null)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'white', borderRadius: 20, padding: '24px 20px',
              width: '80vw', maxWidth: 320, textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>
                {confirmAction.type === 'add' ? '🚬' : '↩️'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                {confirmAction.type === 'add' ? `บันทึก +1 ${unit}?` : `ลบออก 1 ${unit}?`}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                {confirmAction.type === 'add'
                  ? `จำนวนจะเพิ่มเป็น ${todayCount + 1} ${unit}`
                  : `จำนวนจะลดเหลือ ${todayCount - 1} ${unit}`}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setConfirmAction(null)} style={{
                  flex: 1, padding: '12px', borderRadius: 12, border: '2px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                }}>ยกเลิก</button>
                <button onClick={() => {
                  handleLogPuff(confirmAction.type === 'add' ? 1 : -1);
                  setConfirmAction(null);
                }} style={{
                  flex: 1, padding: '12px', borderRadius: 12, border: 'none',
                  background: confirmAction.type === 'add'
                    ? 'linear-gradient(135deg, var(--primary-light), var(--primary))'
                    : 'linear-gradient(135deg, #f87171, #ef4444)',
                  color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                }}>ยืนยัน</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── DATE PICKER MODAL (Range) ── */}
      <Mask visible={calendarOpen} onMaskClick={() => setCalendarOpen(false)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', padding: '0 20px' }}>
          <div style={{ background: 'white', borderRadius: 24, padding: 20, width: '100%', maxWidth: 360, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{
              '--adm-color-primary': theme.primary,
              '--adm-color-text': '#111827',
              '--adm-calendar-header-font-size': '16px',
              '--adm-calendar-title-font-size': '16px',
              '--adm-calendar-day-font-size': '15px',
            } as any}>
              <Calendar
                key={`${tempRangeStart?.getTime()}-${tempRangeEnd?.getTime()}`}
                selectionMode="range"
                defaultValue={tempRangeStart && tempRangeEnd ? [tempRangeStart, tempRangeEnd] : undefined}
                onChange={(val: any) => {
                  if (val && val.length === 2) {
                    setTempRangeStart(val[0]);
                    setTempRangeEnd(val[1]);
                  }
                }}
              />
            </div>

            <div style={{ height: 16 }} />

            {/* Quick select: วันนี้ / 7 วัน / 30 วัน */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { label: 'วันนี้', getRange: () => { const d = new Date(); return [d, d]; } },
                { label: '7 วัน', getRange: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); return [s, e]; } },
                { label: '30 วัน', getRange: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); return [s, e]; } },
              ].map(opt => {
                const [s, e] = opt.getRange();
                const active = tempRangeStart && tempRangeEnd && isSameDayFn(tempRangeStart, s) && isSameDayFn(tempRangeEnd, e);
                return (
                  <button
                    key={opt.label}
                    onClick={() => { const [ns, ne] = opt.getRange(); setTempRangeStart(ns); setTempRangeEnd(ne); }}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 20,
                      border: active ? `1px solid ${theme.primary}` : '1px solid #e5e7eb',
                      background: active ? theme.primary : 'white',
                      color: active ? 'white' : '#4b5563',
                      fontWeight: 600, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setCalendarOpen(false)}
                style={{ flex: 1, padding: '14px 0', borderRadius: 24, border: 'none', background: '#f3f4f6', color: '#4b5563', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
              >
                ยกเลิก
              </button>
              <button
                onClick={() => {
                  if (tempRangeStart && tempRangeEnd) {
                    setRangeStart(tempRangeStart);
                    setRangeEnd(tempRangeEnd);
                  }
                  setCalendarOpen(false);
                }}
                style={{ flex: 1, padding: '14px 0', borderRadius: 24, border: 'none', background: theme.primary, color: 'white', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
              >
                ตกลง
              </button>
            </div>
          </div>
        </div>
      </Mask>
    </>
  );
};
